import React, { useContext, useEffect } from 'react';

import { cardMessages as messages } from '@atlaskit/editor-common/messages';
import type { CardProvider, QuickInsertItem } from '@atlaskit/editor-common/provider-factory';
import {
	IconDatasourceAssetsObjects,
	IconDatasourceConfluenceSearch,
	IconDatasourceJiraIssue,
} from '@atlaskit/editor-common/quick-insert';
import { canRenderDatasource } from '@atlaskit/editor-common/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ASSETS_LIST_OF_LINKS_DATASOURCE_ID } from '@atlaskit/link-datasource/assets-modal';
import { CONFLUENCE_SEARCH_DATASOURCE_ID } from '@atlaskit/link-datasource/confluence-search-modal';
import type { CardContext } from '@atlaskit/link-provider/types';
import { SmartCardContext } from '@atlaskit/link-provider/context';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import type { CardPlugin } from './cardPluginType';
import { blockCardSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/blockCard';
import { embedCardSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/embedCard';
import { inlineCardSpecWithFixedToDOM } from './nodeviews/toDOM-fixes/inlineCard';
import { hideLinkToolbar, setProvider, showDatasourceModal } from './pm-plugins/actions';
import { queueCardsFromChangedTr, queueCardsFromRange } from './pm-plugins/doc';
import { cardKeymap } from './pm-plugins/keymap';
import { createPlugin } from './pm-plugins/main';
import { pluginKey } from './pm-plugins/plugin-key';
import { isDatasourceConfigEditable } from './pm-plugins/utils';
import type { CardPluginOptions } from './types';
import { createEventsQueue } from './ui/analytics/create-events-queue';
import type { CardPluginEvent } from './ui/analytics/types';
import DatasourceModalWithState from './ui/DatasourceModal/ModalWithState';
import { EditorLinkingPlatformAnalytics } from './ui/EditorLinkingPlatformAnalytics';
import { EditorSmartCardEvents } from './ui/EditorSmartCardEvents';
// Ignored via go/ees005
// eslint-disable-next-line import/no-named-as-default
import LayoutButton from './ui/LayoutButton';
import { getPasteDisplayAsMenuComponents } from './ui/PasteDisplayAsMenu';
import { getCardQuickInsertComponents } from './ui/quick-insert/getCardQuickInsertComponents';
import { floatingToolbar, getEndingToolbarItems, getStartingToolbarItems } from './ui/toolbar';

type SmartCardClientRef = {
	current: CardContext['connections']['client'] | undefined;
};

const PasteMenuSmartCardClientSync = ({ clientRef }: { clientRef: SmartCardClientRef }) => {
	const smartCardContext = useContext(SmartCardContext);
	useEffect(() => {
		clientRef.current = smartCardContext?.connections?.client;
	}, [clientRef, smartCardContext?.connections?.client]);
	return null;
};

export const cardPlugin: CardPlugin = ({ config: options = {} as CardPluginOptions, api }) => {
	let previousCardProvider: CardProvider | undefined;
	const cardPluginEvents = createEventsQueue<CardPluginEvent>();
	const isRegisteredSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');
	let instanceEmbedCardTransformers = options.embedCardTransformers;
	let editorViewForPasteMenu: EditorView | undefined;
	const pasteMenuSmartCardClientRef: SmartCardClientRef = { current: undefined };

	const pasteMenuVariant = expValNoExposure(
		'platform_editor_paste_actions_menu_v2',
		'variant',
		'control',
	);
	const shouldRegisterPasteDisplayAsMenu =
		options.enablePasteDisplayAsMenu &&
		['hasSpellingAndGrammar', 'hasAltAiActions'].includes(pasteMenuVariant);
	if (shouldRegisterPasteDisplayAsMenu) {
		api?.uiControlRegistry?.actions.register(
			getPasteDisplayAsMenuComponents({
				api,
				allowBlockCards: options.onlyInlineCards ? false : (options.allowBlockCards ?? true),
				allowEmbeds: options.onlyInlineCards ? false : options.allowEmbeds,
				getEditorView: () => editorViewForPasteMenu,
				smartCardClientRef: pasteMenuSmartCardClientRef,
			}),
		);
	}

	if (isRegisteredSlashCommandEnabled) {
		api?.uiControlRegistry?.actions.register(getCardQuickInsertComponents({ api, options }));
	}

	api?.base?.actions.registerMarks(({ tr, node, pos }) => {
		const { doc } = tr;
		const { schema } = doc.type;
		const { inlineCard: inlineCardNodeType } = schema.nodes;

		if (node.type === inlineCardNodeType) {
			const newText = node.attrs.url;
			const currentPos = tr.mapping.map(pos);
			tr.replaceWith(currentPos, currentPos + node.nodeSize, schema.text(newText, node.marks));
		}
	});

	return {
		name: 'card',

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			return pluginKey.getState(editorState) || null;
		},

		nodes() {
			const nodes = [{ name: 'inlineCard', node: inlineCardSpecWithFixedToDOM() }];

			// `onlyInlineCards` is a hard gate: when set, blockCard/embedCard
			// stay out of the schema regardless of allowBlockCards/allowEmbeds.
			if (options.onlyInlineCards) {
				return nodes;
			}

			nodes.push({ name: 'blockCard', node: blockCardSpecWithFixedToDOM() });

			if (options.allowEmbeds) {
				nodes.push({
					name: 'embedCard',
					node: embedCardSpecWithFixedToDOM(),
				});
			}

			return nodes;
		},

		pmPlugins() {
			// onlyInlineCards forces block/embed off regardless of caller-passed flags,
			// keeping the schema gate (in nodes()) and the runtime gate in sync.
			const allowBlockCards = options.onlyInlineCards ? false : (options.allowBlockCards ?? true);
			const allowEmbeds = options.onlyInlineCards ? false : options.allowEmbeds;
			const allowResizing = options.allowResizing ?? true;
			const useAlternativePreloader = options.useAlternativePreloader ?? true;
			const allowWrapping = options.allowWrapping ?? true;
			const allowAlignment = options.allowAlignment ?? true;
			const allowDatasource = options.allowDatasource ?? false;
			const showUpgradeDiscoverability = options.showUpgradeDiscoverability ?? true;

			const plugins = [
				{
					name: 'card',
					plugin: createPlugin(
						{
							...options,
							allowBlockCards,
							allowEmbeds,
							allowResizing,
							useAlternativePreloader,
							allowWrapping,
							allowAlignment,
							allowDatasource,
							cardPluginEvents,
							embedCardTransformers: instanceEmbedCardTransformers,
							showUpgradeDiscoverability,
						},
						api,
					),
				},
			];

			plugins.push({
				name: 'cardKeymap',
				plugin: ({ featureFlags }) => {
					return cardKeymap(featureFlags);
				},
			});

			return plugins;
		},

		contentComponent({
			editorView,
			popupsMountPoint,
			popupsScrollableElement,
			popupsBoundariesElement,
		}) {
			if (!editorView) {
				return null;
			}
			editorViewForPasteMenu = editorView;

			const breakoutEnabled = options.editorAppearance === 'full-page';
			return (
				<>
					{shouldRegisterPasteDisplayAsMenu && (
						<PasteMenuSmartCardClientSync clientRef={pasteMenuSmartCardClientRef} />
					)}
					<EditorSmartCardEvents editorView={editorView} />
					<EditorLinkingPlatformAnalytics
						cardPluginEvents={cardPluginEvents}
						editorView={editorView}
					/>
					{breakoutEnabled && (
						<LayoutButton
							api={api}
							editorView={editorView}
							mountPoint={popupsMountPoint}
							scrollableElement={popupsScrollableElement}
							boundariesElement={popupsBoundariesElement}
						/>
					)}
					<DatasourceModalWithState api={api} editorView={editorView} />
				</>
			);
		},

		commands: {
			queueCardsFromRange:
				(from, to, source, analyticsAction, normalizeLinkText, sourceEvent, appearance) =>
				({ tr }) =>
					queueCardsFromRange(
						// Synthesise the minimal EditorState shape the impl actually reads.
						{
							schema: tr.doc.type.schema,
							selection: tr.selection,
						} as unknown as Parameters<typeof queueCardsFromRange>[0],
						tr,
						from,
						to,
						source,
						analyticsAction,
						normalizeLinkText,
						sourceEvent,
						appearance,
					),
		},

		actions: {
			setProvider: async (providerPromise) => {
				const provider = await providerPromise;
				// Prevent someone trying to set the exact same provider twice for performance reasons
				if (previousCardProvider === provider || options?.provider === providerPromise) {
					return false;
				}
				previousCardProvider = provider;
				return api?.core.actions.execute(({ tr }) => setProvider(provider)(tr)) ?? false;
			},
			hideLinkToolbar,
			queueCardsFromChangedTr,
			queueCardsFromRange,
			registerEmbedCardTransformer: (transformers) => {
				instanceEmbedCardTransformers = transformers;
			},
			resolveShortLinkUrl: async (url: string) => {
				if (!options.provider) {
					return undefined;
				}
				try {
					const provider = await options.provider;
					// EditorCardProvider holds a CardClient instance internally. We access it
					// directly to call fetchData, which gives us the raw ORS JSON-LD response
					// including the canonical url field — something provider.resolve() does not
					// surface. This is intentionally a structural cast rather than a typed
					// dependency on EditorCardProvider's private API.
					const clientHolder = provider as unknown as {
						cardClient?: { fetchData: (url: string) => Promise<{ data?: unknown }> };
					};
					const client = clientHolder.cardClient;
					if (!client) {
						return undefined;
					}
					const response = await client.fetchData(url);
					// ORS returns a JsonLd.Response whose data union includes BaseData,
					// BaseCollectionData, and BaseCollectionPage. We only care about the
					// `url` field which is present on BaseData (Primitives.Object). Cast
					// to a minimal shape rather than accessing through the full union type.
					const data = response?.data as { url?: unknown } | undefined;
					if (!data) {
						return undefined;
					}
					const urlField = data.url;
					// Property<string | Link> can be a string, a Link object, or an array.
					// Short-link responses always return a plain string URL.
					if (typeof urlField === 'string' && urlField !== url) {
						return urlField;
					}
					// Fallback: Link object shape with href or @id
					if (urlField && typeof urlField === 'object' && !Array.isArray(urlField)) {
						const linkObj = urlField as Record<string, unknown>;
						const id = linkObj['@id'] ?? linkObj['href'];
						if (typeof id === 'string' && id !== url) {
							return id;
						}
					}
				} catch {
					// Swallow errors — callers handle undefined
				}
				return undefined;
			},
			getStartingToolbarItems: getStartingToolbarItems(options, api),
			getEndingToolbarItems: getEndingToolbarItems(options, api),
		},

		pluginsOptions: {
			floatingToolbar: floatingToolbar(
				options,
				options.lpLinkPicker ?? false,
				options.linkPicker,
				api,
				options.disableFloatingToolbar,
			),
			...(isRegisteredSlashCommandEnabled
				? {}
				: {
						quickInsert: ({ formatMessage }) => {
							const quickInsertArray: Array<QuickInsertItem> = [];
							if (!options.allowDatasource) {
								return quickInsertArray;
							}

							quickInsertArray.push({
								id: 'datasource',
								title: formatMessage(
									fg('confluence-issue-terminology-refresh')
										? messages.datasourceJiraIssueIssueTermRefresh
										: messages.datasourceJiraIssue,
								),
								description: formatMessage(
									fg('confluence-issue-terminology-refresh')
										? messages.datasourceJiraIssueDescriptionIssueTermRefresh
										: messages.datasourceJiraIssueDescription,
								),
								isDisabledOffline: true,
								categories: ['external-content', 'development'],
								keywords: ['jira'],
								featured: true,
								...(fg('jim-lower-ranking-in-jira-macro-search') && { priority: 500 }),
								icon: () => <IconDatasourceJiraIssue />,
								action(insert) {
									const tr = insert(undefined);
									showDatasourceModal('jira')(tr);
									return tr;
								},
							});

							if (canRenderDatasource(ASSETS_LIST_OF_LINKS_DATASOURCE_ID)) {
								quickInsertArray.push({
									id: 'datasource',
									title: formatMessage(messages.datasourceAssetsObjectsGeneralAvailability),
									description: formatMessage(messages.datasourceAssetsObjectsDescription),
									isDisabledOffline: true,
									categories: ['external-content', 'development'],
									keywords: ['assets'],
									icon: () => <IconDatasourceAssetsObjects />,
									action(insert) {
										const tr = insert(undefined);
										showDatasourceModal('assets')(tr);
										return tr;
									},
								});
							}

							if (isDatasourceConfigEditable(CONFLUENCE_SEARCH_DATASOURCE_ID)) {
								quickInsertArray.push({
									id: 'datasource',
									title: formatMessage(messages.datasourceConfluenceSearch),
									description: formatMessage(messages.datasourceConfluenceSearchDescription),
									isDisabledOffline: true,
									categories: ['external-content', 'development'],
									keywords: ['confluence'],
									featured: true,
									icon: () => <IconDatasourceConfluenceSearch />,
									action(insert) {
										const tr = insert(undefined);
										showDatasourceModal('confluence-search')(tr);
										return tr;
									},
								});
							}

							return quickInsertArray;
						},
					}),
		},
	};
};
